import React from "react";
import MultiDatePicker, { DateObject } from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import Toolbar from "react-multi-date-picker/plugins/toolbar";
import gregorian from "react-date-object/calendars/gregorian";
import persian from "react-date-object/calendars/persian";
import gregorianEn from "react-date-object/locales/gregorian_en";
import persianFa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/layouts/mobile.css";
import { useMediaQuery, useTheme } from "../../hooks";
import styles from "./styles.module.css";

const pad = (value) => String(value).padStart(2, "0");
const persianFaFullWeekDays = {
  ...persianFa,
  name: "persian_fa_full_weekdays",
  weekDays: persianFa.weekDays.map(([name]) => [name, name]),
};

const toISO = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  ].join("T");
};

const parseISO = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (!value || typeof value !== "string") return null;

  const [datePart, timePart = "00:00"] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour = 0, minute = 0] = timePart.split(":").map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;

  const date = new Date(year, month - 1, day, hour, minute);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toPersianDateObject = (value) => {
  const date = parseISO(value);
  if (!date) return null;

  return new DateObject({
    date,
    calendar: gregorian,
    locale: gregorianEn,
  }).convert(persian, persianFa);
};

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path
      d="M8 2.75v3M16 2.75v3M3.75 9h16.5M7.5 13h.01M12 13h.01M16.5 13h.01M7.5 17h.01M12 17h.01"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <rect
      x="3.5"
      y="4.25"
      width="17"
      height="16.25"
      rx="3.5"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none">
    <path
      d="m4 4 8 8m0-8-8 8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PickerInput = React.forwardRef(
  (
    {
      value,
      placeholder,
      disabled,
      openCalendar,
      onFocus,
      onClear,
      className,
      style,
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={`${styles.field} ${disabled ? styles.fieldDisabled : ""} ${
        className || ""
      }`}
      style={style}
      dir="rtl"
    >
      <span className={styles.calendarIcon} aria-hidden="true">
        <CalendarIcon />
      </span>
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        className={styles.input}
        aria-label={placeholder}
        onFocus={onFocus}
        onClick={openCalendar}
      />
      {value && !disabled && (
        <button
          type="button"
          className={styles.clearButton}
          aria-label="پاک کردن تاریخ"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            onClear();
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  ),
);

PickerInput.displayName = "PickerInput";

export default function JDatePicker({
  value,
  onChange,
  placeholder = "تاریخ را انتخاب کنید",
  disabled = false,
  className,
  style,
  size = "small",
  filledGray = false,
  fillColor = "#f5f5f5",
  compact = false,
  responsive = true,
  customHeight,
  customWidth,
  /* Corner radius, for callers that sit in a panel with a house radius of its
     own — the task modal's property rows are all 6px. Defaults to the
     component's own 8px. */
  radius,
  showEvents = true,
  showTime = false,
  fullWidth = false,
  /* Which side the calendar opens on. Left alone the calendar always drops
     downwards and, because `fixMainPosition` keeps it there, a field low in a
     modal opened its calendar off the bottom of the screen where the dates
     could not be reached. Callers that sit low pass "top-start". */
  calendarPosition = "bottom-start",
}) {
  const { isDark } = useTheme();
  const isMobileViewport = useMediaQuery("(max-width: 992px)");
  const isMobile = responsive && isMobileViewport;
  const pickerRef = React.useRef(null);

  // Retained for API compatibility with the previous shared component.
  void showEvents;

  const pickerValue = React.useMemo(() => toPersianDateObject(value), [value]);
  const fieldHeight = customHeight ?? (size === "large" && !isMobile ? 40 : 38);
  const fieldWidth = customWidth ?? (fullWidth ? "100%" : compact ? 115 : "auto");
  const fieldBackground = filledGray
    ? isDark
      ? "var(--ds-field-bg)"
      : fillColor
    : "var(--ds-surface)";

  const emitChange = React.useCallback(
    (selected) => {
      if (!selected) {
        onChange?.(null);
        return;
      }

      const nextDate = new DateObject(selected)
        .convert(gregorian, gregorianEn)
        .toDate();

      if (!showTime) nextDate.setHours(0, 0, 0, 0);
      onChange?.(toISO(nextDate));
    },
    [onChange, showTime],
  );

  const plugins = React.useMemo(
    () => [
      showTime ? (
        <TimePicker
          key="time"
          position="bottom"
          hideSeconds
          className={styles.timePicker}
        />
      ) : null,
      !isMobile ? (
        <Toolbar
          key="toolbar"
          position="bottom"
          sort={["close", "today", "deselect"]}
          names={{
            close: "تأیید",
            today: "امروز",
            deselect: "پاک کردن",
          }}
          className={styles.toolbar}
        />
      ) : null,
    ].filter(Boolean),
    [isMobile, showTime],
  );

  const inputStyle = {
    "--jdp-height": `${fieldHeight}px`,
    "--jdp-background": fieldBackground,
    "--jdp-hover-background": filledGray
      ? "var(--ds-field-bg-hover)"
      : "var(--ds-surface-2)",
    "--jdp-border-color": filledGray ? "transparent" : "var(--ds-border)",
    "--jdp-active-border-color": filledGray
      ? "transparent"
      : "var(--ds-accent)",
    "--jdp-font-size": compact || isMobile ? "12px" : "13px",
    ...(radius === undefined ? {} : { "--jdp-radius": `${radius}px` }),
    width: "100%",
    ...style,
  };

  return (
    <div
      className={styles.root}
      style={{ width: fieldWidth, maxWidth: "100%" }}
      dir="rtl"
    >
      <MultiDatePicker
        ref={pickerRef}
        value={pickerValue}
        onChange={emitChange}
        calendar={persian}
        locale={persianFaFullWeekDays}
        format={showTime ? "YYYY/MM/DD HH:mm" : "YYYY/MM/DD"}
        plugins={plugins}
        disabled={disabled}
        editable={false}
        readOnly={disabled}
        inputMode="none"
        portal
        zIndex={5000}
        arrow={false}
        calendarPosition={calendarPosition}
        fixMainPosition
        onOpenPickNewDate={false}
        className={`${styles.calendar} ${isMobile ? "rmdp-mobile" : ""}`}
        containerClassName={styles.pickerContainer}
        containerStyle={{ width: "100%" }}
        mobileLabels={{ OK: "تأیید", CANCEL: "انصراف" }}
        render={
          <PickerInput
            placeholder={placeholder}
            disabled={disabled}
            onClear={() => emitChange(null)}
            className={className}
            style={inputStyle}
          />
        }
      />
    </div>
  );
}
